import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, X } from 'lucide-react';
import { UserRole, WS_EVENT, type OrderMessagePayload } from '@kick/types';
import { api, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useEventoSocket } from '@/lib/socket';
import { Boton } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

interface Mensaje {
  id: string;
  senderId: string;
  senderRole: UserRole;
  body: string;
  readAt: string | null;
  createdAt: string;
}

/**
 * Cosas que un cliente escribe siempre.
 *
 * Están aquí porque escribir en el celular con una mano mientras se espera en
 * la puerta es incómodo, y porque así los mensajes llegan cortos y claros: el
 * domiciliario los lee de un vistazo en un semáforo.
 */
const ATAJOS = [
  'Puedes dejarlo en portería',
  'Ya bajo, dame un minuto',
  '¿Cuánto te falta?',
  'Timbra en el apartamento',
];

export function ChatPedido({
  orderId,
  nombreDomiciliario,
  abierto,
  onCerrar,
}: {
  orderId: string;
  nombreDomiciliario: string;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const cliente = useQueryClient();
  const avisos = useAvisos();
  const [texto, setTexto] = useState('');
  const final = useRef<HTMLDivElement>(null);

  const mensajes = useQuery({
    queryKey: ['mensajes', orderId],
    queryFn: async () => (await api.get<Mensaje[]>(`/pedidos/${orderId}/mensajes`)).data,
    enabled: abierto,
  });

  const enviar = useMutation({
    mutationFn: async (body: string) =>
      (await api.post<Mensaje>(`/pedidos/${orderId}/mensajes`, { body })).data,
    onSuccess: () => {
      setTexto('');
      void cliente.invalidateQueries({ queryKey: ['mensajes', orderId] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  // Lo que llega en vivo entra sin recargar, y solo si es de este pedido.
  useEventoSocket<OrderMessagePayload>(WS_EVENT.ORDER_MESSAGE, (payload) => {
    if (payload.orderId !== orderId) return;
    void cliente.invalidateQueries({ queryKey: ['mensajes', orderId] });
  });

  /**
   * Siempre abajo, donde está lo último.
   *
   * Una conversación que se abre por el primer mensaje obliga a desplazarse
   * para leer lo que acaba de llegar, que es justo lo que se venía a ver.
   */
  useEffect(() => {
    if (abierto) final.current?.scrollIntoView({ block: 'end' });
  }, [abierto, mensajes.data]);

  if (!abierto) return null;

  const lista = mensajes.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCerrar} aria-hidden />

      <div
        role="dialog"
        aria-label={`Chat con ${nombreDomiciliario}`}
        className="relative flex h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-t-kick bg-white sm:h-[70vh] sm:rounded-kick"
      >
        <header className="flex items-center justify-between border-b border-kick-gray/15 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate font-bold text-kick-dark">{nombreDomiciliario}</p>
            <p className="text-xs text-kick-gray">Tu domiciliario</p>
          </div>
          <button
            onClick={onCerrar}
            aria-label="Cerrar el chat"
            className="rounded-kick p-2 text-kick-gray transition hover:bg-kick-light"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 space-y-2 overflow-y-auto bg-kick-light p-4">
          {mensajes.isLoading && (
            <p className="text-center text-sm text-kick-gray">Cargando…</p>
          )}

          {!mensajes.isLoading && lista.length === 0 && (
            <p className="mx-auto max-w-xs text-center text-sm text-kick-gray">
              Escríbele si necesitas darle alguna indicación para llegar.
            </p>
          )}

          {lista.map((m) => {
            const mio = m.senderRole === UserRole.CUSTOMER;
            return (
              <div key={m.id} className={cn('flex', mio ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[80%] rounded-kick px-3 py-2 text-sm',
                    mio ? 'bg-kick-red text-white' : 'bg-white text-kick-ink',
                  )}
                >
                  <p>{m.body}</p>
                  <p className={cn('mt-0.5 text-[11px]', mio ? 'text-white/70' : 'text-kick-gray')}>
                    {new Date(m.createdAt).toLocaleTimeString('es-CO', {
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: 'America/Bogota',
                    })}
                  </p>
                </div>
              </div>
            );
          })}

          <div ref={final} />
        </div>

        <div className="border-t border-kick-gray/15 bg-white p-3">
          {lista.length === 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {ATAJOS.map((atajo) => (
                <button
                  key={atajo}
                  onClick={() => enviar.mutate(atajo)}
                  disabled={enviar.isPending}
                  className="rounded-full border border-kick-gray/25 px-3 py-1.5 text-xs text-kick-ink transition hover:border-kick-red hover:text-kick-red"
                >
                  {atajo}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (texto.trim()) enviar.mutate(texto.trim());
            }}
            className="flex gap-2"
          >
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              maxLength={500}
              placeholder="Escribe un mensaje…"
              aria-label="Mensaje"
              className="min-w-0 flex-1 rounded-kick border border-kick-gray/30 px-3 py-2.5 text-sm text-kick-ink outline-none transition focus:border-kick-red"
            />
            <Boton
              type="submit"
              className="px-3"
              disabled={!texto.trim()}
              cargando={enviar.isPending}
              icono={<Send className="h-4 w-4" />}
            >
              <span className="sr-only">Enviar</span>
            </Boton>
          </form>
        </div>
      </div>
    </div>
  );
}
